import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import * as signalR from '@microsoft/signalr';
import { AuthService } from '@/app/auth/services/auth.service';
import { NotificationService } from './notification.service';
import { AppConfigService } from './app-config.service';
import { LocalStorageService } from '../storage/local-storage.service';
import { MyNotificationsViewModel } from '@/app/proxy/proxies';

interface NegotiateResponse {
    url: string;
    accessToken: string;
}

@Injectable({
    providedIn: 'root',
})
export class NotificationSignalrService implements OnDestroy {
    private connection?: signalR.HubConnection;
    private authSubscription?: Subscription;
    private bootstrapped = false;
    
    // Dedicated stream for SignalR notifications (for test page)
    public signalrNotifications$ = new BehaviorSubject<MyNotificationsViewModel[]>([]);

    constructor(
        private httpClient: HttpClient,
        private localStorageService: LocalStorageService,
        private authService: AuthService,
        private notificationService: NotificationService,
        private appConfigService: AppConfigService
    ) {
        // No longer auto-connect in constructor to avoid race conditions with app config loading
    }

    ngOnDestroy(): void {
        this.disconnect();
        if (this.authSubscription) {
            this.authSubscription.unsubscribe();
        }
    }

    /**
     * Bootstrap the SignalR connection.
     * Should be called once from PagesComponent (for returning users with valid tokens)
     * or from LoginComponent (after successful login).
     * Idempotent - safe to call multiple times.
     */
    public async bootstrap(): Promise<void> {
        if (this.bootstrapped) {
            return;
        }
        this.bootstrapped = true;

        await this.appConfigService.initializeApp();

        // Connect immediately if authenticated
        if (this.authService.isAuthenticated()) {
            await this.connect();
        }

        // Listen for auth state changes
        this.authSubscription = this.authService.userSession$
            .pipe(
                distinctUntilChanged((prev, curr) => {
                    const prevId = prev?.id || null;
                    const currId = curr?.id || null;
                    return prevId === currId;
                })
            )
            .subscribe((userSession) => {
                const isAuthenticatedNow = this.authService.isAuthenticated();
                const hasActiveConnection = !!this.connection;

                if (isAuthenticatedNow && !hasActiveConnection) {
                    void this.connect();
                } else if (!isAuthenticatedNow && hasActiveConnection) {
                    this.disconnect();
                }
            });
    }

    private async connect(): Promise<void> {
        // Guard: must be authenticated
        if (!this.authService.isAuthenticated()) {
            console.log('[SignalR] Cannot connect: not authenticated');
            return;
        }

        // Ensure app configuration is loaded before any notification or negotiate request.
        await this.appConfigService.initializeApp();

        // Step 1: Always fetch initial notification history (even if SignalR can't connect)
        console.log('[SignalR] Fetching initial notification history...');
        this.notificationService.fetchNotifications().subscribe({
            next: (notifications) => {
                this.notificationService.newNotifications$.next(notifications);
                console.log(`[SignalR] Loaded ${notifications.length} notifications from history`);
            },
            error: (error) => {
                console.error('[SignalR] Error fetching initial notifications:', error);
            },
        });

        // Guard: check if SignalR negotiate URL is configured
        const negotiateUrl = this.appConfigService.signalrNegotiateUrl;
        if (!negotiateUrl) {
            console.log('[SignalR] Cannot connect: Core.Notification.Negotiate.URL config key not found');
            return;
        }

        // Already connected
        if (this.connection) {
            console.log('[SignalR] Already connected');
            return;
        }

        try {

            // Step 2: Negotiate with the Function to get SignalR connection info
            console.log(`[SignalR] Negotiating with ${negotiateUrl}...`);
            const token = this.localStorageService.getItem<string>('token');
            const negotiateResponse = await this.httpClient
                .post<NegotiateResponse>(negotiateUrl, null, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                .toPromise();

            if (!negotiateResponse || !negotiateResponse.url || !negotiateResponse.accessToken) {
                console.error('[SignalR] Invalid negotiate response:', negotiateResponse);
                return;
            }

            console.log(`[SignalR] Negotiated successfully. Connecting to ${negotiateResponse.url}...`);

            // Step 3: Build SignalR connection
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(negotiateResponse.url, {
                    accessTokenFactory: () => negotiateResponse.accessToken,
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            // Step 4: Register event handlers
            this.connection.on('ReceiveNotification', (payload: any) => {
                console.log('[SignalR] ReceiveNotification:', payload);
                const notification = MyNotificationsViewModel.fromJS(payload);
                
                // Add to main notification service (for header bell, etc.)
                this.notificationService.addRealtimeNotification(notification);
                
                // Also add to SignalR-specific stream (for test page)
                const currentSignalrNotifications = this.signalrNotifications$.value;
                const updatedSignalrNotifications = [notification, ...currentSignalrNotifications];
                this.signalrNotifications$.next(updatedSignalrNotifications);
            });

            this.connection.onreconnecting((error) => {
                console.log('[SignalR] Reconnecting...', error?.message);
            });

            this.connection.onreconnected((connectionId) => {
                console.log(`[SignalR] Reconnected. ConnectionId: ${connectionId}`);
                // Re-fetch history to catch any notifications missed while offline
                this.notificationService.fetchNotifications().subscribe({
                    next: (notifications) => {
                        this.notificationService.newNotifications$.next(notifications);
                        console.log(`[SignalR] Re-fetched ${notifications.length} notifications after reconnect`);
                    },
                    error: (error) => {
                        console.error('[SignalR] Error re-fetching notifications after reconnect:', error);
                    },
                });
            });

            this.connection.onclose((error) => {
                console.log('[SignalR] Connection closed', error?.message);
                this.connection = undefined;
            });

            // Step 5: Start the connection
            await this.connection.start();
            console.log(`[SignalR] Connected. ConnectionId: ${this.connection.connectionId}`);
        } catch (error) {
            console.error('[SignalR] Connection error:', error);
            this.connection = undefined;
        }
    }

    private async disconnect(): Promise<void> {
        if (this.connection) {
            try {
                await this.connection.stop();
                console.log('[SignalR] Disconnected');
            } catch (error) {
                console.error('[SignalR] Error disconnecting:', error);
            } finally {
                this.connection = undefined;
            }
        }
    }

    /**
     * Get connection status for debugging
     */
    public getConnectionStatus(): {
        isConnected: boolean;
        connectionId: string | null;
        state: string;
    } {
        return {
            isConnected: this.connection?.state === signalR.HubConnectionState.Connected,
            connectionId: this.connection?.connectionId || null,
            state: this.connection?.state?.toString() || 'Disconnected',
        };
    }
}
